import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import {
  ChangePriorityDialog,
  PlaceOnHoldDialog,
  CancelConsignmentDialog,
  useConsignmentActions
} from '@/features/consignment-actions';
import { ShowDispositionDialog } from '@/features/show-disposition';

import type { ConsignmentTableData } from '@/entities/consignments';
import { DataTable, type DataTableProps } from '@/shared/ui/data-table';

import { buildConsignmentRowActions } from '../lib/build-row-actions';

interface ConsignmentsDataTableProps extends Omit<DataTableProps<ConsignmentTableData>, 'actions' | 'onRowAction'> {
  onClearSelection?: () => void;
}

export function ConsignmentsDataTable({
  selectedIds = [],
  onSelectionChange,
  onClearSelection,
  onExport,
  ...dataTableProps
}: ConsignmentsDataTableProps) {
  const { t } = useTranslation();
  const { actions, dialogs } = useConsignmentActions({
    selectedIds,
    onClearSelection
  });

  const rowActions = useMemo(() => buildConsignmentRowActions({ actions, t }), [actions, t]);

  return (
    <>
      <DataTable
        {...dataTableProps}
        selectedIds={selectedIds}
        onSelectionChange={onSelectionChange}
        actions={rowActions}
        onExport={onExport}
      />

      <ChangePriorityDialog
        open={dialogs.changePriority.open}
        onOpenChange={dialogs.changePriority.onOpenChange}
        onConfirm={(priority, comments) => dialogs.changePriority.onConfirm(priority, comments)}
      />

      <PlaceOnHoldDialog
        open={dialogs.placeOnHold.open}
        onOpenChange={dialogs.placeOnHold.onOpenChange}
        onConfirm={(remarks) => dialogs.placeOnHold.onConfirm(remarks)}
      />

      <CancelConsignmentDialog
        open={dialogs.cancel.open}
        onOpenChange={dialogs.cancel.onOpenChange}
        onConfirm={(reason, remarks) => dialogs.cancel.onConfirm(reason, remarks)}
      />

      <ShowDispositionDialog
        open={dialogs.showDisposition.open}
        onOpenChange={dialogs.showDisposition.onOpenChange}
        consignmentId={dialogs.showDisposition.consignmentId}
      />
    </>
  );
}
